import { Avatar, Button, Divider, Flex, HStack, Text } from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivitySuggestion, Category, ConfirmActivitiesSuggestedList } from '../../services/network'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'

interface ISuggestedActivityProps {
  activity: ActivitySuggestion
  category: Category | undefined
  confirmSuggestedActivitiesList: ConfirmActivitiesSuggestedList | undefined
  setConfirmSuggestedActivitiesList: React.Dispatch<React.SetStateAction<ConfirmActivitiesSuggestedList | undefined>>
  isLoading: boolean
}

function SuggestedActivity({ activity, category, setConfirmSuggestedActivitiesList, isLoading }: ISuggestedActivityProps): JSX.Element {
  const { t } = useTranslation()
  const [isApproved, setIsApproved] = useState(false)
  const prevIsApprovedRef = useRef(isApproved)
  const [isRejected, setIsRejected] = useState(false)
  const prevIsRejectedRef = useRef(isRejected)

  const handleDeleteButton = (): void => {
    setIsRejected(true)
  }

  const handleConfirmButton = (): void => {
    setIsApproved(true)
  }

  const handleCancelDeleteButton = (): void => {
    setIsRejected(false)
  }

  const handleCancelApproveButton = (): void => {
    setIsApproved(false)
  }

  useEffect((): void => {
    eva.replace()
  }, [isApproved, isRejected, isLoading])

  useEffect((): void => {
    const prevIsRejected = prevIsRejectedRef.current

    if (!prevIsRejected && isRejected) {
      // isRejected changed from false to true, add activity
      setConfirmSuggestedActivitiesList((prevList) => ({
        confirmActivitiesSuggested: [
          ...(prevList?.confirmActivitiesSuggested || []),
          { activitySuggestionId: activity.id, accepted: false, userProposerId: activity.user.id }
        ]
      }))
    } else if (prevIsRejected && !isRejected) {
      // isRejected changed from true to false, remove activity
      setConfirmSuggestedActivitiesList((prevList) => ({
        confirmActivitiesSuggested: prevList?.confirmActivitiesSuggested.filter((activityL) => activityL.activitySuggestionId !== activity.id) || []
      }))
    }

    // Update the ref with the current isRejected value
    prevIsRejectedRef.current = isRejected
  }, [activity.id, activity.user.id, isRejected, setConfirmSuggestedActivitiesList])

  useEffect((): void => {
    const prevIsApproved = prevIsApprovedRef.current

    if (!prevIsApproved && isApproved) {
      // add activity
      setConfirmSuggestedActivitiesList((prevList) => ({
        confirmActivitiesSuggested: [
          ...(prevList?.confirmActivitiesSuggested || []),
          { activitySuggestionId: activity.id, accepted: true, userProposerId: activity.user.id }
        ]
      }))
    } else if (prevIsApproved && !isApproved) {
      // isRejected changed from true to false, remove activity
      setConfirmSuggestedActivitiesList((prevList) => ({
        confirmActivitiesSuggested: prevList?.confirmActivitiesSuggested.filter((activityL) => activityL.activitySuggestionId !== activity.id) || []
      }))
    }
    prevIsApprovedRef.current = isApproved
  }, [activity.id, activity.user.id, isApproved, setConfirmSuggestedActivitiesList])

  return (
    <Flex marginBottom={'10px'} marginRight={'10px'} direction={'column'} justifyContent={'flex-start'}>
      <Flex>
        <Flex direction={'column'} maxWidth={'200px'} maxHeight={'250px'} borderRadius={'12px'} bg={'NeutralLight'}>
          <Avatar height={'56px'} width={'56px'} margin={'10px'} name='avatar' src={activity.user.profileImageUrl}></Avatar>
          <Flex direction={'column'}>
            <Text ml={'10px'} fontSize='16px' fontWeight='600' lineHeight='22.4px'>
              {activity.user.firstName}
            </Text>
            <Text ml={'10px'} fontSize='16px' fontWeight='600' lineHeight='22.4px'>
              {activity.user.lastName}
            </Text>
          </Flex>
          <Flex bg={'rgba(143, 155, 179, 0.16)'} borderRadius={'3px'} gap={'8px'} width={'102px'} height={'32px'} margin={'10px'}>
            <HStack>
              <Text ml={'10px'} fontSize='14px' fontWeight='600' lineHeight='19.6px'>
                {activity.user.activityAccepted}/{activity.user.activitySuggested}
              </Text>
              <Text fontSize='12px' fontWeight='300' lineHeight='14px'>
                {t('accepted')}
              </Text>
            </HStack>
          </Flex>
        </Flex>
        <Flex direction={'column'} width={'100%'} height={'116px'} marginLeft={'25px'}>
          <HStack marginTop={'15px'}>
            <Text fontSize='16px' fontWeight='600' lineHeight='22.4px'>
              {activity.name.it}
              <span style={{ marginLeft: '16px', fontSize: '16px', fontWeight: '700', lineHeight: '22.4px', color: '#B0BBC4', textTransform: 'uppercase' }}>
                {category ? category.description.it : activity.categoryId}
              </span>
            </Text>
          </HStack>
          <Text fontSize='14px' fontWeight='400' lineHeight='19.6px'>
            {activity.description}
          </Text>
        </Flex>
        {!isLoading && !isApproved && !isRejected && (
          <Flex gap={'10px'} alignItems={'center'}>
            <Button
              key={'buttonDelete'}
              onClick={(): void => {
                handleDeleteButton()
              }}
              borderRadius={'17px'}
              padding={'12px, 16px, 12px, 16px'}
              gap={'10px'}
              width={'56px'}
              height={'52px'}
              border={'1px solid #4476EF'}>
              <i data-eva={'close-circle-outline'} data-eva-fill={'#4476EF'} data-eva-height={'28px'} data-eva-width={'24px'}></i>
            </Button>
            <Button
              key={'buttonConfirm'}
              onClick={(): void => handleConfirmButton()}
              background={'#4476EF'}
              borderRadius={'17px'}
              padding={'12px, 16px, 12px, 16px'}
              gap={'10px'}
              width={'56px'}
              height={'52px'}>
              <i data-eva={'checkmark-circle-2-outline'} data-eva-fill={'white'} data-eva-height={'28px'} data-eva-width={'24px'}></i>
            </Button>
          </Flex>
        )}
        {!isLoading && isApproved && (
          <Flex gap={'10px'} alignItems={'center'}>
            {
              <Button onClick={handleCancelApproveButton}>
                <Text fontSize='12px' fontWeight='600' lineHeight='16.8px' color={'#54636F'}>
                  {t('cancel')}
                </Text>
              </Button>
            }
            <Text variant={'Accepted'} alignContent={'center'} height={'22px'}>
              {t('Accepted')}
            </Text>
          </Flex>
        )}
        {!isLoading && isRejected && (
          <Flex gap={'10px'} alignItems={'center'}>
            {
              <Button onClick={handleCancelDeleteButton}>
                <Text fontSize='12px' fontWeight='600' lineHeight='16.8px' color={'#54636F'}>
                  {t('cancel')}
                </Text>
              </Button>
            }
            <Text variant={'Refused'} alignContent={'center'} height={'22px'}>
              {t('Refused')}
            </Text>
          </Flex>
        )}
      </Flex>
      <Divider orientation='horizontal' marginTop={'10px'} />
    </Flex>
  )
}

export { SuggestedActivity }
